import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * UtilitiesComparisonChart
 * - Shows Paid vs Billed Utility amounts per period
 * - Paid: sum of actual linked expenses with expense_category === 'utilities' across provided assetIds
 * - Billed: stacked by electric/gas/other, fetched from property utility-bill-series endpoint
 */
export default function UtilitiesComparisonChart({ propertyId, assetIds = [], onRangeChange = null, paidTxns = null, showBilled = true }) {
  const [aggregation, setAggregation] = useState('monthly');
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), 0, 1).toISOString().slice(0,10);
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), 11, 31).toISOString().slice(0,10);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paidActuals, setPaidActuals] = useState([]); // [{date, amount, category}]
  const [billedSeries, setBilledSeries] = useState([]); // [{date, electric, gas, other, water, recycling, total}]
  const [utilitiesSet, setUtilitiesSet] = useState(new Set());
  const [labelMap, setLabelMap] = useState({}); // expense category code -> human label
  const [cumulative, setCumulative] = useState(false);

  // Load expense category choices to determine Utilities parent and children
  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
        const data = Array.isArray(res.data) ? res.data : [];
        const set = new Set();
        const map = {};
        // Helper to title-case fallback
        const toTitle = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        // If API returns tree objects: [{ value, label, children: [...] }]
        if (data.length && typeof data[0] === 'object' && data[0] !== null && ('value' in data[0] || 'children' in data[0])) {
          const items = data;
          const visit = (node) => {
            if (!node) return;
            const v = node.value != null ? String(node.value) : '';
            const lbl = node.label != null ? String(node.label) : (v ? toTitle(v) : '');
            if (v) { map[v] = lbl; }
            if (v === 'utilities') set.add('utilities');
            if (v && v.startsWith('utilities')) set.add(v);
            if (Array.isArray(node.children)) node.children.forEach(visit);
          };
          items.forEach(visit);
          // Extract children of utilities specifically
          const parent = items.find(it => String(it.value) === 'utilities');
          if (parent && Array.isArray(parent.children)) {
            for (const ch of parent.children) {
              if (ch && ch.value != null) set.add(String(ch.value));
            }
          }
        } else {
          // If API returns flat pairs like [[value,label], ...]
          for (const pair of data) {
            try {
              const val = String(Array.isArray(pair) ? pair[0] : pair?.value);
              const lbl = Array.isArray(pair) ? String(pair[1]) : String(pair?.label || toTitle(val));
              if (val) map[val] = lbl;
              if (val && (val === 'utilities' || val.startsWith('utilities'))) set.add(val);
            } catch (e) { /* ignore */ }
          }
        }
        // Explicit known utility subcategory codes to ensure coverage
        const KNOWN = ['water','gas','electric','electricity','electric_gas','other','utilities__water','utilities__gas','utilities__electric','utilities__other'];
        const KNOWN_LABELS = {
          utilities: 'Utilities',
          water: 'Utilities - Water',
          gas: 'Utilities - Gas',
          electric: 'Utilities - Electric',
          electricity: 'Utilities - Electric',
          electric_gas: 'Utilities - Electric & Gas',
          other: 'Utilities - Other',
          utilities__water: 'Utilities - Water',
          utilities__gas: 'Utilities - Gas',
          utilities__electric: 'Utilities - Electric',
          utilities__other: 'Utilities - Other',
        };
        for (const k of KNOWN) {
          set.add(k);
          if (!map[k]) map[k] = KNOWN_LABELS[k] || toTitle(k);
        }
        if (!set.size) {
          // Ensure at least parent
          set.add('utilities');
          if (!map['utilities']) map['utilities'] = 'Utilities';
        }
        setUtilitiesSet(set);
        setLabelMap(map);
      } catch (e) {
        // Fallback: treat anything starting with 'utilities' as Utilities and include known codes
        const set = new Set(['utilities','water','gas','electric','electricity','electric_gas','utilities__water','utilities__gas','utilities__electric','utilities__other']);
        const map = {
          utilities: 'Utilities', water: 'Utilities - Water', gas: 'Utilities - Gas', electric: 'Utilities - Electric', electricity: 'Utilities - Electric',
          electric_gas: 'Utilities - Electric & Gas', utilities__water: 'Utilities - Water', utilities__gas: 'Utilities - Gas', utilities__electric: 'Utilities - Electric', utilities__other: 'Utilities - Other', other: 'Utilities - Other'
        };
        setUtilitiesSet(set);
        setLabelMap(map);
      }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!propertyId) return;
      setLoading(true);
      setError('');
      try {
        // Derive Paid from provided paidTxns if available (preferred to mirror the table exactly)
        if (Array.isArray(paidTxns)) {
          const rows = [];
          for (const t of paidTxns) {
            try {
              // Date: prefer transaction_date, then post_date, then date
              const dt = t?.transaction_date || t?.post_date || t?.date;
              if (!dt) continue;
              // Amount: match table semantics — treat expenses as positive magnitudes
              const amt = Math.abs(Number(t?.amount)) || 0;
              // Category filter already applied in parent; still guard if categories loaded
              rows.push({ date: dt, amount: amt, category: String(t?.expense_category || '') });
            } catch (e) { /* ignore bad row */ }
          }
          setPaidActuals(rows);
        } else {
          // Fallback: Fetch paid actuals from linked-expenses for assetIds, filter to utilities
          const calls = (assetIds || []).map((aid) =>
            axios.get(`/api/balance_sheet/assets/physical_assets/${aid}/linked-expenses/`, { params: { start_date: fromDate, end_date: toDate, only_txn_backed: 1 } })
              .then(r => r.data).catch(()=>({ actual: [] }))
          );
          const results = await Promise.all(calls);
          const rows = [];
          for (const res of results) {
            const actual = Array.isArray(res?.actual) ? res.actual : [];
            for (const it of actual) {
              const cat = String(it?.expense_category || it?.txn_category || '');
              if (utilitiesSet.has(cat) && it?.date != null) {
                rows.push({ date: it.date, amount: Number(it.amount)||0, category: cat });
              }
            }
          }
          setPaidActuals(rows);
        }

        // Fetch billed series only if enabled
        if (showBilled) {
          const b = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/utility-bill-series/`, {
            params: { start_date: fromDate, end_date: toDate, aggregation }
          });
          setBilledSeries(Array.isArray(b?.data?.series) ? b.data.series : []);
        } else {
          setBilledSeries([]);
        }
      } catch (e) {
        setError('Failed to load utilities data.');
        setPaidActuals([]);
        setBilledSeries([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [propertyId, JSON.stringify(assetIds), fromDate, toDate, aggregation, JSON.stringify(Array.from(utilitiesSet)), Array.isArray(paidTxns) ? paidTxns.length : 'no-paid', showBilled]);

  useEffect(() => {
    if (typeof onRangeChange === 'function') {
      try { onRangeChange(fromDate, toDate); } catch(e){}
    }
  }, [fromDate, toDate, onRangeChange]);

  const { labels, paidSeries, billedElectric, billedGas, billedOther, billedWater, billedRecycling, paidBreakdown } = useMemo(() => {
    // Build uniform period labels between from/to
    const range = [];
    let start = moment(fromDate);
    const end = moment(toDate);
    const step = aggregation === 'weekly' ? 'week' : (aggregation === 'monthly' ? 'month' : (aggregation === 'yearly' ? 'year' : 'day'));
    while (start.isSameOrBefore(end)) {
      range.push(start.startOf(step).format('YYYY-MM-DD'));
      start.add(1, step);
    }
    const paidMap = {};
    const paidBreakdown = {}; // { period: { code: sum } }
    for (const it of paidActuals||[]) {
      if (!it?.date) continue;
      const key = moment(it.date).startOf(step).format('YYYY-MM-DD');
      const amt = Number(it.amount) || 0;
      paidMap[key] = (paidMap[key] || 0) + amt;
      const code = String(it.category || 'utilities');
      if (!paidBreakdown[key]) paidBreakdown[key] = {};
      paidBreakdown[key][code] = (paidBreakdown[key][code] || 0) + amt;
    }
    const billedMap = {};
    for (const it of billedSeries||[]) {
      const key = String(it.date);
      billedMap[key] = {
        electric: Number(it.electric)||0,
        gas: Number(it.gas)||0,
        other: Number(it.other)||0,
        water: Number(it.water)||0,
        recycling: Number(it.recycling)||0,
      };
    }
    const paidSeries = range.map(d => paidMap[d] || 0);
    const billedElectric = range.map(d => billedMap[d]?.electric || 0);
    const billedGas = range.map(d => billedMap[d]?.gas || 0);
    const billedOther = range.map(d => billedMap[d]?.other || 0);
    const billedWater = range.map(d => billedMap[d]?.water || 0);
    const billedRecycling = range.map(d => billedMap[d]?.recycling || 0);
    return { labels: range, paidSeries, billedElectric, billedGas, billedOther, billedWater, billedRecycling, paidBreakdown };
  }, [paidActuals, billedSeries, aggregation, fromDate, toDate]);

  const data = useMemo(() => {
    const cumulate = (arr) => {
      const out = [];
      let run = 0;
      for (let i = 0; i < arr.length; i++) {
        run += Number(arr[i]) || 0;
        out.push(run);
      }
      return out;
    };
    const paidData = cumulative ? cumulate(paidSeries) : paidSeries;
    const billedElecData = cumulative ? cumulate(billedElectric) : billedElectric;
    const billedGasData = cumulative ? cumulate(billedGas) : billedGas;
    const billedOtherData = cumulative ? cumulate(billedOther) : billedOther;
    const billedWaterData = cumulative ? cumulate(billedWater) : billedWater;
    const billedRecyclingData = cumulative ? cumulate(billedRecycling) : billedRecycling;
    const datasets = [
      {
        label: 'Paid (transactions tagged Utilities)',
        data: paidData,
        backgroundColor: 'rgba(59,130,246,0.7)',
        borderColor: 'rgba(59,130,246,1)',
        borderWidth: 1,
        stack: 'paid',
        order: 1,
      },
    ];
    if (showBilled) {
      datasets.push(
        {
          label: 'Billed - Electric',
          data: billedElecData,
          backgroundColor: 'rgba(34,197,94,0.6)',
          borderColor: 'rgba(34,197,94,1)',
          borderWidth: 1,
          stack: 'billed',
          order: 2,
        },
        {
          label: 'Billed - Gas',
          data: billedGasData,
          backgroundColor: 'rgba(234,179,8,0.6)',
          borderColor: 'rgba(234,179,8,1)',
          borderWidth: 1,
          stack: 'billed',
          order: 2,
        },
        {
          label: 'Billed - Water',
          data: billedWaterData,
          backgroundColor: 'rgba(59,130,246,0.6)',
          borderColor: 'rgba(59,130,246,1)',
          borderWidth: 1,
          stack: 'billed',
          order: 2,
        },
        {
          label: 'Billed - Recycling',
          data: billedRecyclingData,
          backgroundColor: 'rgba(147,197,253,0.6)',
          borderColor: 'rgba(147,197,253,1)',
          borderWidth: 1,
          stack: 'billed',
          order: 2,
        },
        {
          label: 'Billed - Other',
          data: billedOtherData,
          backgroundColor: 'rgba(239,68,68,0.6)',
          borderColor: 'rgba(239,68,68,1)',
          borderWidth: 1,
          stack: 'billed',
          order: 2,
        }
      );
    }
    return { labels, datasets };
  }, [labels, paidSeries, billedElectric, billedGas, billedOther, billedWater, billedRecycling, showBilled, cumulative]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { color: '#e5e7eb' } },
      tooltip: {
        backgroundColor: '#111827',
        titleColor: '#e5e7eb',
        bodyColor: '#e5e7eb',
        callbacks: {
          title: (items) => {
            if (!items?.length) return '';
            return items[0].label || '';
          },
          afterBody: (items) => {
            if (!items?.length) return [];
            const idx = items[0].dataIndex;
            const chart = items[0].chart;
            const ds = chart?.data?.datasets || [];
            const paid = Number(ds?.[0]?.data?.[idx]) || 0;
            const label = items[0].label || '';
            const period = label; // labels[] are ISO dates per period
            const lines = [];
            // Compute billed components shown (respect cumulative toggle via dataset values)
            const hasBilled = !!showBilled && ds.length >= 2;
            // Find billed datasets by label to avoid depending on fixed indices
            const valByLabel = (lbl) => {
              try {
                const dsi = ds.find(d => String(d.label).toLowerCase() === String(lbl).toLowerCase());
                return Number(dsi?.data?.[idx]) || 0;
              } catch(e) { return 0; }
            };
            const e = hasBilled ? valByLabel('Billed - Electric') : 0;
            const g = hasBilled ? valByLabel('Billed - Gas') : 0;
            const w = hasBilled ? valByLabel('Billed - Water') : 0;
            const r = hasBilled ? valByLabel('Billed - Recycling') : 0;
            const o = hasBilled ? valByLabel('Billed - Other') : 0;
            const billedTotal = hasBilled ? (e + g + w + r + o) : 0;
            // 2) Paid breakdown by sub-category for this period (non-cumulative by design)
            try {
              const bd = paidBreakdown?.[period] || {};
              const entries = Object.entries(bd).filter(([,v]) => (Number(v)||0) !== 0).sort((a,b)=> Number(b[1]) - Number(a[1]));
              if (entries.length) {
                for (const [code, val] of entries) {
                  const human = labelMap[code] || (String(code).replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()));
                  lines.push(`${human}: $${Number(val).toFixed(2)}`);
                }
              } else {
                lines.push(`Paid: $${paid.toFixed(2)}`);
              }
            } catch (e) {
              lines.push(`Paid: $${paid.toFixed(2)}`);
            }
            // Spacer between Paid breakdown and Billed section (only if billed shown)
            if (hasBilled) {
              lines.push('');
              lines.push(`Billed - Electric: $${e.toFixed(2)}`);
              lines.push(`Billed - Gas: $${g.toFixed(2)}`);
              if (w) lines.push(`Billed - Water: $${w.toFixed(2)}`);
              if (r) lines.push(`Billed - Recycling: $${r.toFixed(2)}`);
              lines.push(`Billed - Other: $${o.toFixed(2)}`);
              // Spacer between billed sub-categories and billed total
              lines.push('');
              lines.push(`Billed Total: $${billedTotal.toFixed(2)}`);
            }
            return lines;
          }
        }
      }
    },
    scales: {
      x: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
      y: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
    },
  }), [paidSeries, billedElectric, billedGas, billedOther, billedWater, billedRecycling, showBilled, cumulative]);

  return (
    <div style={{ height: 400, background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 10 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
        <div style={{ color:'#e5e7eb', fontWeight:600 }}>Utilities — Paid vs Billed</div>
        <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
          <select className="form-select form-select-sm" value={aggregation} onChange={(e)=> setAggregation(e.target.value)} style={{ width: 140 }}>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
            <option value="yearly">Yearly</option>
          </select>
          <input className="form-control form-control-sm" type="date" value={fromDate} onChange={e=> setFromDate(e.target.value)} />
          <input className="form-control form-control-sm" type="date" value={toDate} onChange={e=> setToDate(e.target.value)} />
          <div className="form-check form-switch" style={{ display:'flex', alignItems:'center', gap:6 }}>
            <input className="form-check-input" type="checkbox" id="utilCumSwitch" checked={cumulative} onChange={(e)=> setCumulative(e.target.checked)} />
            <label className="form-check-label" htmlFor="utilCumSwitch" style={{ color:'#e5e7eb' }}>Cumulative</label>
          </div>
        </div>
      </div>
      {loading ? (
        <div style={{ color:'#9ca3af' }}>Loading…</div>
      ) : error ? (
        <div className="alert alert-warning" style={{ background:'transparent', color:'#fbbf24' }}>{error}</div>
      ) : (
        <div style={{ height: 340 }}>
          <Bar data={data} options={options} height={340} />
        </div>
      )}
    </div>
  );
}
